const STORAGE_KEY = 'kl_rate_limit_block';
export const RATE_LIMIT_BLOCKED_EVENT = 'rate-limit-blocked';
export const RATE_LIMIT_CLEARED_EVENT = 'rate-limit-cleared';

import { buildApiUrl } from './apiBaseUrl';

/**
 * @typedef {{
 *   blockedUntil: string,
 *   limit?: number | null,
 *   windowSeconds?: number | null,
 *   tier?: string | null,
 * }} RateLimitBlockState
 */

/**
 * @param {RateLimitBlockState | null | undefined} state
 * @returns {RateLimitBlockState | null}
 */
export function normalizeRateLimitBlock(state) {
  if (!state?.blockedUntil) {
    return null;
  }

  const expiresAt = new Date(state.blockedUntil);
  if (Number.isNaN(expiresAt.getTime()) || expiresAt.getTime() <= Date.now()) {
    return null;
  }

  return {
    blockedUntil: expiresAt.toISOString(),
    limit: state.limit ?? null,
    windowSeconds: state.windowSeconds ?? 60,
    tier: state.tier ?? null,
  };
}

/**
 * @param {RateLimitBlockState} state
 */
export function saveRateLimitBlock(state) {
  const normalized = normalizeRateLimitBlock(state);
  if (!normalized) {
    clearRateLimitBlock();
    return;
  }

  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
  window.dispatchEvent(
    new CustomEvent(RATE_LIMIT_BLOCKED_EVENT, { detail: normalized })
  );
}

/**
 * @returns {RateLimitBlockState | null}
 */
export function readRateLimitBlock() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    const normalized = normalizeRateLimitBlock(parsed);
    if (!normalized) {
      clearRateLimitBlock();
      return null;
    }
    return normalized;
  } catch {
    return null;
  }
}

export function clearRateLimitBlock() {
  sessionStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent(RATE_LIMIT_CLEARED_EVENT));
}

/**
 * @param {import('./rateLimit').RateLimitError} error
 */
export function persistRateLimitFromError(error) {
  const blockedUntil =
    error.blockedUntil ||
    new Date(Date.now() + (error.retryAfterSeconds || 60) * 1000).toISOString();

  saveRateLimitBlock({
    blockedUntil,
    limit: error.limit,
    windowSeconds: error.windowSeconds,
    tier: error.tier,
  });
}

/**
 * @returns {Promise<RateLimitBlockState | null>}
 */
export async function fetchRateLimitStatus() {
  try {
    const response = await fetch(buildApiUrl('/api/auth/rate-limit-status'), {
      method: 'GET',
      credentials: 'include',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      return null;
    }

    const result = await response.json();
    if (!result?.success || !result?.data?.blocked || !result?.data?.data) {
      clearRateLimitBlock();
      return null;
    }

    const block = normalizeRateLimitBlock({
      blockedUntil: result.data.data.blockedUntil,
      limit: result.data.data.limit,
      windowSeconds: result.data.data.windowSeconds,
      tier: result.data.data.tier,
    });

    if (block) {
      saveRateLimitBlock(block);
      return block;
    }

    clearRateLimitBlock();
    return null;
  } catch {
    return readRateLimitBlock();
  }
}

/**
 * @param {URLSearchParams} searchParams
 * @returns {RateLimitBlockState | null}
 */
export function readRateLimitBlockFromSearchParams(searchParams) {
  const blockedUntil = searchParams.get('blockedUntil');
  if (!blockedUntil) {
    return null;
  }

  const limit = Number.parseInt(searchParams.get('limit') || '', 10);
  const windowSeconds = Number.parseInt(searchParams.get('window') || '', 10);

  return normalizeRateLimitBlock({
    blockedUntil,
    limit: Number.isFinite(limit) && limit > 0 ? limit : null,
    windowSeconds:
      Number.isFinite(windowSeconds) && windowSeconds > 0 ? windowSeconds : 60,
    tier: searchParams.get('tier'),
  });
}
